'use client'

import React from 'react'
import { cn, openInNewTab } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'

// ---------------------------------------------------------------------------
// DidYouMeanCard
//
// Renders when a claim/sample lookup found no exact match but the backend
// ranked some near-misses. Turns a dead end into a pick-one.
//
// Two affordances per option, deliberately:
//   * the row itself continues the conversation (asks the AI about that
//     record) — the common case, and it keeps the user in the chat;
//   * the icon button opens the record's page in a new tab, matching
//     ActionChipRow's existing new-tab convention.
//
// Read-only: selecting an option issues a search, never a mutation.
// ---------------------------------------------------------------------------

export interface DidYouMeanOption {
  rank: number
  value: string
  _ui_link?: string
  match_reason?: string
  score?: number
}

interface DidYouMeanCardProps {
  query: string
  entityType?: string
  options: DidYouMeanOption[]
  onSelect: (message: string) => void
  disabled?: boolean
}

function entityLabel(entityType?: string): string {
  switch (entityType) {
    case 'claim':  return 'claim'
    case 'sample': return 'sample'
    default:       return 'record'
  }
}

export function DidYouMeanCard({
  query,
  entityType,
  options,
  onSelect,
  disabled = false,
}: DidYouMeanCardProps) {
  if (!options || options.length === 0) return null

  const label = entityLabel(entityType)

  const handleSelect = (option: DidYouMeanOption) => {
    if (disabled) return
    // Phrased as an explicit, unambiguous lookup so the follow-up turn hits
    // the exact-match path rather than re-running the fuzzy search.
    onSelect(`Show me ${label} ${option.value}`)
  }

  const handleOpen = (event: React.MouseEvent, option: DidYouMeanOption) => {
    // Stop the row's own handler firing as well.
    event.stopPropagation()
    if (!option._ui_link) return
    openInNewTab(option._ui_link)
  }

  return (
    <div
      className={cn(
        'mt-2 rounded-xl overflow-hidden',
        'bg-gradient-to-br from-amber-50/80 to-transparent',
        'border border-amber-200/60',
      )}
    >
      {/* Header — states the miss before offering anything */}
      <div className="flex items-start gap-2.5 px-4 pt-3 pb-2">
        <div className={cn(
          'flex items-center justify-center w-7 h-7 rounded-lg shrink-0 mt-0.5',
          'bg-amber-100 text-amber-700',
        )}>
          <Icons.search className="h-3.5 w-3.5" strokeWidth={1.75} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-brand-navy">
            No {label} found matching{' '}
            <span className="font-semibold break-all">&ldquo;{query}&rdquo;</span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Did you mean one of these?
          </p>
        </div>
      </div>

      {/* Ranked options — order comes from the backend, already scored */}
      <ul className="px-2 pb-2 space-y-1">
        {options.map((option) => (
          <li key={`${option.rank}-${option.value}`}>
            <div
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-disabled={disabled}
              onClick={() => handleSelect(option)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleSelect(option)
                }
              }}
              className={cn(
                'flex items-center gap-2.5 px-2.5 py-2 rounded-lg w-full text-left',
                'bg-white/60 border border-transparent',
                'transition-all duration-150 group',
                disabled
                  ? 'opacity-60 cursor-not-allowed'
                  : cn(
                      'cursor-pointer hover:bg-white',
                      'hover:border-brand-cornflower/40',
                      'focus:outline-none focus-visible:ring-2',
                      'focus-visible:ring-brand-cornflower/60',
                    ),
              )}
            >
              {/* Rank badge — lets the user reply "2" in the chat instead */}
              <span
                aria-hidden="true"
                className={cn(
                  'flex items-center justify-center w-5 h-5 shrink-0 rounded-md',
                  'bg-brand-navy/10 text-brand-navy',
                  'text-[10px] font-semibold tabular-nums',
                )}
              >
                {option.rank}
              </span>

              <span className="min-w-0 flex-1">
                <span className="block text-sm text-brand-navy font-medium truncate">
                  {option.value}
                </span>
                {option.match_reason && (
                  <span className="block text-[11px] text-muted-foreground truncate">
                    {option.match_reason}
                  </span>
                )}
              </span>

              {option._ui_link && (
                <button
                  type="button"
                  onClick={(e) => handleOpen(e, option)}
                  disabled={disabled}
                  title={`Open ${option.value} in a new tab`}
                  aria-label={`Open ${option.value} in a new tab`}
                  className={cn(
                    'shrink-0 p-1 rounded-md text-muted-foreground',
                    'hover:text-brand-cornflower hover:bg-brand-cornflower/10',
                    'focus:outline-none focus-visible:ring-2',
                    'focus-visible:ring-brand-cornflower/60',
                    'transition-colors',
                  )}
                >
                  <Icons.externalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <p className="px-4 pb-3 text-[11px] text-muted-foreground">
        Select an option, or reply with its number.
      </p>
    </div>
  )
}
